import { useState, useEffect, ChangeEvent, KeyboardEventHandler } from 'react';
import { Input } from '@trackman/web-shared-components';
import { t } from 'i18next';

// types
import { ValidationType } from 'types/GlobalTypes';

type PropsType = {
  label: string;
  name: string;
  value: string | number | Date;
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  type?: string;
  // TODO: change validationRules to be able to accept a message along with the validation function
  // validationRules?: Array<{ errorMsg: string; isValid: ValidationType }>;
  validationRules?: Array<ValidationType>;
  hint?: string;
  width?: string;
  disabled?: boolean;
  InputProps?: any;
  id?: string;
  validateOnMount?: boolean;
  onKeyDown?: (e) => void;
  className?: string;
};

const AppInput = ({
  label,
  name,
  type = 'text',
  value = '',
  validationRules = [],
  hint = '',
  onChange,
  width,
  disabled = false,
  InputProps,
  onKeyDown,
  id,
  validateOnMount = false,
  className,
}: PropsType) => {
  const [isTouched, setIsTouchedState] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [initialValue, setInitialValue] = useState<string | number | Date>(value ?? '');

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsTouchedState(true);
    isValueInvalid(e.target.value);
    onChange?.(e);
  };

  const isValueInvalid = (value: string): Array<string | boolean> => {
    if (!isTouched) return [];

    const errors = validationRules.map((rule) => rule(value)).filter(Boolean);

    setIsInvalid(!!errors.length);

    if (errors.length) setErrorMessage(errors.map((err) => t(`error.${err}`)).join(', ') || t('error.invalid_value'));
    if (!errors.length) setErrorMessage('');

    return errors;
  };

  // validate after is touched
  useEffect(() => {
    if (isTouched && typeof value === 'string') isValueInvalid(value);
  }, [isTouched]);

  useEffect(() => {
    if (initialValue) setIsTouchedState(true);
  }, [initialValue]);

  useEffect(() => {
    if (validateOnMount) setIsTouchedState(true);
  }, [validateOnMount]);

  return (
    <Input
      id={id}
      label={label}
      name={name || ''}
      value={value}
      type={type}
      error={isInvalid}
      hint={errorMessage || hint}
      onChange={(e) => handleChange(e)}
      width={width}
      inputProps={{
        className: `${className} ${isInvalid ? 'app-input--is-invalid' : ''}`,
        'data-test': name ? `${name}-input` : 'app-input',
        autoComplete: 'off',
      }}
      InputProps={InputProps}
      disabled={!onChange || disabled}
      onKeyDown={onKeyDown}
    />
  );
};

export default AppInput;
